import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Toast } from '../components/Toast';
import { EmptyState } from '../components/EmptyState';
import { CoachingSowForm } from '../components/sow/CoachingSowForm';
import { CohortContractForm } from '../components/sow/CohortContractForm';
import { ExecCoachingSowForm } from '../components/sow/ExecCoachingSowForm';
import { ExecutivePartnerSowForm } from '../components/sow/ExecutivePartnerSowForm';
import { VersionHistory } from '../components/sow/VersionHistory';
import { generateSow, getSowPrefill } from '../lib/api';
import { fmt } from '../lib/format';
import { generateSowSchema } from '../../../src/reports/sow/validation';
import { SowErrorsContext } from '../components/sow/ValidationFields';
import { SowRequestError, sowFieldErrors } from '../lib/sow-validation';
import { SOW_TEMPLATE_LABELS as TEMPLATE_LABEL } from '../lib/sow';
import type { SowFieldValues, SowTemplateType } from '../types';

export function SowEditor(): JSX.Element {
  const { oppId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const templateParam = (searchParams.get('template') as SowTemplateType | null) ?? undefined;

  const { data: prefill, isLoading, error } = useQuery({
    queryKey: ['sow-prefill', oppId, templateParam ?? 'auto'],
    queryFn: () => getSowPrefill(oppId, templateParam),
    staleTime: Infinity, // form edits own the state after load; don't clobber
  });

  const [fields, setFields] = useState<SowFieldValues | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Adopt fresh prefill whenever a new one arrives (template switch / new opp).
  useEffect(() => {
    if (prefill) { setFields(prefill.fields); setFieldErrors({}); setGenerationError(null); }
  }, [prefill]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const activeTemplate = fields?.templateType ?? templateParam ?? prefill?.suggestedTemplate;

  function switchTemplate(t: SowTemplateType) {
    if (t === activeTemplate) return;
    if (fields && !window.confirm(`Switch to the ${TEMPLATE_LABEL[t]} template? Unsaved edits are discarded.`)) return;
    setFields(null);
    setSearchParams(t === prefill?.suggestedTemplate ? {} : { template: t });
  }

  async function onGenerate() {
    if (!fields || !prefill) return;
    const validation = generateSowSchema.safeParse({ opportunitySfId: prefill.meta.opportunity.id, fields });
    if (!validation.success) {
      setFieldErrors(sowFieldErrors(validation.error.issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message }))));
      setGenerationError('Please correct the highlighted fields.');
      return;
    }
    setFieldErrors({});
    setGenerationError(null);
    setGenerating(true);
    try {
      const result = await generateSow(prefill.meta.opportunity.id, fields);
      setToast({ kind: 'ok', msg: `${result.fileName} generated${result.version ? ` (V${result.version})` : ''}` });
      qc.invalidateQueries({ queryKey: ['sow-docs'] });
      if (result.downloadUrl) window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setGenerationError(err instanceof Error ? err.message : 'Generate failed');
      if (err instanceof SowRequestError) setFieldErrors(sowFieldErrors(err.details));
    } finally {
      setGenerating(false);
    }
  }

  useEffect(() => {
    const first = Object.keys(fieldErrors)[0];
    if (!first) return;
    const control = Array.from(document.querySelectorAll<HTMLElement>('[name]')).find((el) => el.getAttribute('name') === first);
    (control ?? document.getElementById('sow-generation-error'))?.focus();
    control?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [fieldErrors]);

  const opp = prefill?.meta.opportunity;

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd" style={{ paddingBottom: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="crumb">
              <Link to="/sow">SOWs</Link>
              <span style={{ margin: '0 6px', color: 'var(--border-2)' }}>/</span>
              <span className="d-mono">{opp?.number ?? oppId}</span>
            </div>
            <div className="eb">{activeTemplate ? TEMPLATE_LABEL[activeTemplate] : 'SOW editor'}</div>
            <h1 className="d-title" style={{ marginTop: 2 }}>
              {opp ? (opp.accountName ?? opp.name ?? 'Opportunity') : 'Loading opportunity…'}
            </h1>
            {opp && (
              <div className="d-meta" style={{ marginTop: 10, fontSize: 13 }}>
                {opp.name}
                {opp.stageName && <><span style={{ margin: '0 10px', color: 'var(--border-2)' }}>·</span>{opp.stageName}</>}
                {opp.amount != null && <><span style={{ margin: '0 10px', color: 'var(--border-2)' }}>·</span><span className="d-tnum">{fmt.cur(opp.amount)}</span></>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button variant="primary" icon="download" onClick={onGenerate} disabled={!fields || generating}>
              {generating ? 'Generating…' : 'Generate .docx'}
            </Button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px' }}>
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 24, color: 'var(--mute)' }}>
              <span className="spinner" /> Loading opportunity, products, contacts, and coach from Salesforce…
            </div>
          )}

          {Boolean(error) && !isLoading && (
            <EmptyState
              icon="alert"
              title="Couldn't load this opportunity"
              body={error instanceof Error ? error.message : 'Unknown error'}
              cta={<Button variant="secondary" onClick={() => navigate('/sow')}>Back to search</Button>}
            />
          )}

          {prefill && fields && (
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div className="segmented">
                  {(Object.keys(TEMPLATE_LABEL) as SowTemplateType[]).map((t) => (
                    <button key={t} type="button" className={activeTemplate === t ? 'on' : ''} onClick={() => switchTemplate(t)}>
                      {TEMPLATE_LABEL[t]}
                      {prefill.suggestedTemplate === t ? ' ✓' : ''}
                    </button>
                  ))}
                </div>
                <span style={{ fontSize: 12.5, color: 'var(--mute)' }}>
                  Suggested: {TEMPLATE_LABEL[prefill.suggestedTemplate]} — {prefill.detection.reason} ({prefill.detection.confidence} confidence)
                </span>
              </div>

              {fields.templateType === 'executive_partner_sow' && <div className="banner warn">Executive Partner is awaiting Danielle’s review. Verify fees and fields before using this coach scope.</div>}

              {prefill.gaps.length > 0 && (
                <div className="banner warn">
                  <span className="dot" />
                  <span>
                    <strong>{prefill.gaps.length} field{prefill.gaps.length === 1 ? '' : 's'} need attention:</strong>{' '}
                    {prefill.gaps.map((g) => g.message).join(' · ')}
                  </span>
                </div>
              )}

              {generationError && <div id="sow-generation-error" tabIndex={-1} className="banner err" role="alert">
                <div>{generationError}{Object.entries(fieldErrors).map(([path, message]) => <div key={path}>{path}: {message}</div>)}</div>
              </div>}
              <SowErrorsContext.Provider value={fieldErrors}>
              {fields.templateType === 'coaching_sow' ? (
                <CoachingSowForm
                  fields={fields}
                  contacts={prefill.meta.accountContacts}
                  tierPresets={prefill.meta.coachingTierPresets}
                  standardDesiredOutcomes={prefill.meta.standardDesiredOutcomes}
                  onChange={setFields}
                />
              ) : fields.templateType === 'exec_coaching_sow' ? (
                <ExecCoachingSowForm
                  fields={fields}
                  scopeDefaults={prefill.meta.execCoachingScopeDefaults}
                  tierPresets={prefill.meta.coachingTierPresets}
                  onChange={setFields}
                />
              ) : fields.templateType === 'executive_partner_sow' ? (
                <ExecutivePartnerSowForm fields={fields} onChange={setFields} />
              ) : (
                <CohortContractForm fields={fields} onChange={setFields} />
              )}

              </SowErrorsContext.Provider>
              <VersionHistory opportunitySfId={prefill.meta.opportunity.id} />
            </div>
          )}
        </div>
      </div>

      {toast && <Toast kind={toast.kind}>{toast.msg}</Toast>}
    </div>
  );
}
